(() => {
  // ---- CONFIG
  const SLIDER_SEL = '.card-image-slider';
  const LIST_SEL   = '.card-image-slider__list';
  const FILL_SEL   = '.card-image-slider__progress-fill';
  const BAR_SEL    = '.card-image-slider__progress';
  const PREV_SEL   = '.card-image-slider__btn--prev';
  const NEXT_SEL   = '.card-image-slider__btn--next';
  const MAX_STEPS  = 5; // progress segments cap (4 image slides + the metafield video slide)
  const HOLD_MAX_MS = 1000; // longest an arrow's scroll may hold the index (see bind())

  // ---- INTERNAL STATE
  const instances = new Map(); // sliderEl -> instance
  let ticking = false;

  // ---- UTIL
  const scheduleTick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      instances.forEach(inst => {
        if (!inst.dirty) return;
        inst.dirty = false;
        flushUI(inst);
      });
      // If anything marked dirty during flush, schedule again
      if ([...instances.values()].some(i => i.dirty)) scheduleTick();
    });
  };

  // An arrow at either end is marked aria-disabled, never `disabled`. A button
  // that becomes disabled while it has focus drops focus to <body> (Chrome and
  // Safari), so the key press that reached the last image left a keyboard user
  // nowhere, with the arrows gone (WCAG 2.4.3, 2.4.7). tabindex="-1" keeps an
  // end arrow out of the Tab order exactly as `disabled` did, but an arrow that
  // already has focus keeps it. Pressing it does nothing: scrollByStep() has no
  // step to take. A `disabled` in the markup is cleared here. Writes only on
  // change, since this runs on every flush.
  const setBtnState = (btn, atEnd) => {
    if (!btn) return;
    if (btn.disabled) btn.disabled = false;
    const aria = atEnd ? 'true' : 'false';
    if (btn.getAttribute('aria-disabled') !== aria) btn.setAttribute('aria-disabled', aria);
    if (atEnd && btn.getAttribute('tabindex') !== '-1') btn.setAttribute('tabindex', '-1');
    else if (!atEnd && btn.hasAttribute('tabindex')) btn.removeAttribute('tabindex');
    btn.classList.toggle('card-image-slider__btn--disabled', atEnd);
  };

  // ---- OBSERVERS (shared)
  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const slide = entry.target;
      const slider = slide.__cardSliderParent;
      const inst = instances.get(slider);
      if (!inst || inst.held >= 0) continue; // an arrow's scroll is under way; see bind()
      const idx = Number(slide.getAttribute('data-step-index')) || 0;
      if (idx !== inst.index) {
        inst.index = idx;
        inst.dirty = true;
      }
    }
    scheduleTick();
  }, { threshold: [0.5] });

  // ---- UI
  function flushUI(inst) {
    const { list, bar, fill, steps, maxIndex, index, segmentPct } = inst;
    const stepPos = maxIndex === 0 ? 0 : Math.round((index / maxIndex) * (steps - 1));
    fill.style.width = `${segmentPct}%`;
    fill.style.transform = `translateX(${stepPos * 100}%)`;
    bar.style.display = steps > 1 ? '' : 'none';
    setBtnState(inst.prevBtn, index <= 0);
    setBtnState(inst.nextBtn, index >= maxIndex);
  }

  function bind(slider) {
    if (instances.has(slider)) return;

    const list = slider.querySelector(LIST_SEL);
    const fill = slider.querySelector(FILL_SEL);
    const bar  = slider.querySelector(BAR_SEL);
    if (!list || !fill || !bar) return;

    const slides = Array.from(list.querySelectorAll('.slider__slide'));
    const steps = Math.max(1, Math.min(MAX_STEPS, slides.length));
    const maxIndex = Math.max(0, slides.length - 1);
    const segmentPct = 100 / steps;

    // Annotate + observe slides
    slides.forEach((s, i) => {
      s.setAttribute('data-step-index', i);
      s.__cardSliderParent = slider;
      io.observe(s);
    });

    const prevBtn = slider.querySelector(PREV_SEL);
    const nextBtn = slider.querySelector(NEXT_SEL);

    const inst = {
      slider, list, bar, fill, slides,
      prevBtn, nextBtn,
      steps, maxIndex, segmentPct,
      index: 0,
      held: -1, // index an arrow's scroll is heading to, -1 when none
      holdTimer: 0,
      dirty: true
    };
    instances.set(slider, inst);

    // The index comes from the scroll position for any scroll the arrows did not
    // start: swipes, trackpads, and Tab moving focus onto a slide link.
    const syncFromScroll = () => {
      const w = list.clientWidth || 1;
      const idx = Math.max(0, Math.min(maxIndex, Math.round(list.scrollLeft / w)));
      if (idx !== inst.index) {
        inst.index = idx;
        inst.dirty = true;
        scheduleTick();
      }
    };

    // An arrow press scrolls smoothly, and syncFromScroll() would re-derive the
    // index from every position on the way. That flipped the arrows back and
    // forth mid-trip: the Next that had just reached the end was re-enabled on
    // the first frame, then disabled again. So the index is held at the press's
    // target until the list gets there, the same idea as card-loop-video.js's
    // `homing`. The timer lets go of a trip that never arrives (a swipe takes
    // over) and re-syncs from wherever the list stopped.
    const releaseHold = () => {
      clearTimeout(inst.holdTimer);
      inst.held = -1;
      syncFromScroll();
    };

    // Controls
    const scrollByStep = (dir) => {
      const w = list.clientWidth || 0;
      if (!w) return;
      const target = Math.max(0, Math.min(maxIndex, inst.index + dir));
      if (target === inst.index) return;
      inst.index = target;
      inst.held = target;
      clearTimeout(inst.holdTimer);
      inst.holdTimer = setTimeout(releaseHold, HOLD_MAX_MS);
      inst.dirty = true;
      scheduleTick();
      list.scrollTo({ left: target * w, behavior: 'smooth' });
    };

    // A pointer click that reaches an end lets go of focus there, as `disabled`
    // used to. Chrome leaves focus on a clicked button, and a mouse user's next
    // key would land on the end arrow: an arrow key showed its ring, and Space
    // pressed it instead of scrolling the page. Enter, Space and assistive
    // technology click with detail 0, and keep focus.
    const onArrowClick = (e, dir) => {
      e.preventDefault();
      scrollByStep(dir);
      const btn = e.currentTarget;
      const atEnd = dir < 0 ? inst.index <= 0 : inst.index >= maxIndex;
      if (atEnd && e.detail !== 0 && document.activeElement === btn) btn.blur();
    };

    prevBtn && prevBtn.addEventListener('click', e => onArrowClick(e, -1));
    nextBtn && nextBtn.addEventListener('click', e => onArrowClick(e, 1));

    // Scroll sync (throttled to 1/frame)
    let scrollRAF = 0;
    list.addEventListener('scroll', () => {
      if (scrollRAF) return;
      scrollRAF = requestAnimationFrame(() => {
        scrollRAF = 0;
        if (inst.held < 0) {
          syncFromScroll();
          return;
        }
        // Once the position rounds to the target, the rest of the trip agrees
        // with it, so the hold can go.
        if (Math.round(list.scrollLeft / (list.clientWidth || 1)) === inst.held) releaseHold();
      });
    }, { passive: true });

    // Resize sync
    let resizeRAF = 0;
    const onResize = () => {
      if (resizeRAF) return;
      resizeRAF = requestAnimationFrame(() => {
        resizeRAF = 0;
        inst.dirty = true;
        scheduleTick();
      });
    };
    window.addEventListener('resize', onResize);
    inst.onResize = onResize; // stored so the MutationObserver cleanup can remove it

    // Images loading can shift layout
    slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', () => {
        inst.dirty = true;
        scheduleTick();
      }, { once: true });
    });

    // Initialize
    list.scrollLeft = 0;
    slider.setAttribute('data-progress-bound', 'true');
    scheduleTick();
  }

  function initAll(root = document) {
    root.querySelectorAll(`${SLIDER_SEL}:not([data-progress-bound])`).forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  // Re-init on section loads
  document.addEventListener('shopify:section:load', e => initAll(e.target || document));

  // Watch only likely containers (avoid whole document for perf)
  const moRoot = document.querySelector('#main-content') || document.body;
  const mo = new MutationObserver(muts => {
    let needsInit = false;
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(SLIDER_SEL)) { bind(n); }
        else if (n.querySelector && n.querySelector(SLIDER_SEL)) { needsInit = true; }
      }
      // Cleanup removed sliders to avoid leaks
      for (const n of m.removedNodes) {
        if (n.nodeType !== 1) continue;
        const gone = n.matches?.(SLIDER_SEL) ? [n] : Array.from(n.querySelectorAll?.(SLIDER_SEL) || []);
        gone.forEach(sl => {
          const inst = instances.get(sl);
          if (!inst) return;
          // Release the external references that otherwise keep the detached
          // slider (and its whole subtree) alive across AJAX filter swaps: the
          // per-slider window resize listener and the shared IntersectionObserver's
          // slide observations. Internal listeners (scroll/click/img-load) are on
          // the subtree itself and get GC'd once these external refs are gone.
          if (inst.onResize) window.removeEventListener('resize', inst.onResize);
          clearTimeout(inst.holdTimer);
          if (inst.slides) inst.slides.forEach(s => { io.unobserve(s); delete s.__cardSliderParent; });
          instances.delete(sl);
        });
      }
    }
    if (needsInit) initAll(moRoot);
  });
  mo.observe(moRoot, { childList: true, subtree: true });

  // Optional: pause work when tab hidden (saves battery)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleTick();
  });
})();